import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import * as FS from "../lib/fileSystem";
import { X, Terminal, FolderOpen, Activity, Globe, FolderPlus, FileText, Trash2, Pencil, Loader2, Sparkles, ShieldCheck, Monitor, Download, Plug, Bot, Zap, Clock, Play, Plus, CheckCircle2, XCircle, Send } from "lucide-react";

const TABS = [
  { key: "command", label: "Command", icon: Terminal },
  { key: "agent", label: "Agent", icon: Bot },
  { key: "automations", label: "Automate", icon: Zap },
  { key: "devices", label: "Devices", icon: Monitor },
  { key: "files", label: "Files", icon: FolderOpen },
  { key: "history", label: "History", icon: Activity },
];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function ControlCenter({ open, onClose, initialTab, initialGoal }) {
  const [tab, setTab] = useState("command");
  const [cmdText, setCmdText] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dir, setDir] = useState(null);
  const [entries, setEntries] = useState([]);
  const [newFolder, setNewFolder] = useState("");
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [devices, setDevices] = useState([]);
  const [pairCode, setPairCode] = useState(null);
  // Agent tasks
  const [goal, setGoal] = useState("");
  const [planning, setPlanning] = useState(false);
  const [agentTask, setAgentTask] = useState(null);
  const [agentTasks, setAgentTasks] = useState([]);
  // Automations
  const [autos, setAutos] = useState([]);
  const [showAutoForm, setShowAutoForm] = useState(false);
  const [autoForm, setAutoForm] = useState({ title: "", mode: "daily", time: "09:00", days: [0], interval: 60, action: "notify" });

  const loadDevices = useCallback(async () => {
    try { const { data } = await api.get("/devices"); setDevices(data.devices); } catch {}
  }, []);
  const genPairCode = async () => { const { data } = await api.post("/devices/pair-code"); setPairCode(data.code); loadDevices(); };
  const togglePerm = async (d, k) => {
    const perms = { ...d.permissions, [k]: !d.permissions[k] };
    const { data } = await api.put(`/devices/${d.id}/permissions`, { permissions: perms });
    setDevices((x) => x.map((i) => (i.id === d.id ? data.device : i)));
  };
  const removeDevice = async (id) => { await api.delete(`/devices/${id}`); setDevices((x) => x.filter((i) => i.id !== id)); };

  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get("/activity/history"); setHistory(data.history); } catch {}
  }, []);
  const loadAgentTasks = useCallback(async () => {
    try { const { data } = await api.get("/agent-tasks"); setAgentTasks(data.tasks); } catch {}
  }, []);
  const loadAutos = useCallback(async () => {
    try { const { data } = await api.get("/automations"); setAutos(data.automations); } catch {}
  }, []);

  useEffect(() => { if (open && tab === "history") loadHistory(); }, [open, tab, loadHistory]);
  useEffect(() => { if (open && tab === "devices") loadDevices(); }, [open, tab, loadDevices]);
  useEffect(() => { if (open && tab === "agent") loadAgentTasks(); }, [open, tab, loadAgentTasks]);
  useEffect(() => { if (open && tab === "automations") loadAutos(); }, [open, tab, loadAutos]);
  useEffect(() => { if (open && initialTab) setTab(initialTab); }, [open, initialTab]);
  useEffect(() => { if (open && initialGoal) { setTab("agent"); setGoal(initialGoal); } }, [open, initialGoal]);

  const planAgent = async () => {
    if (!goal.trim()) return;
    setPlanning(true); setAgentTask(null);
    try {
      const { data } = await api.post("/agent-tasks/plan", {
        goal: goal.trim(), client_now: new Date().toISOString(),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setAgentTask(data.task);
      if (!data.task.steps?.length) toast("I couldn't turn that into a runnable plan.");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "insufficient_credits") toast.error("Not enough Stealth Credits to plan this.");
      else if (d === "daily_automation_cap_reached") toast.error("You've hit today's automation credit cap.");
      else if (d === "plan_exceeds_per_run_cap") toast.error("That plan would cost more than the per-run limit.");
      else toast.error("Couldn't plan that.");
    } finally { setPlanning(false); }
  };

  const approveAgent = async () => {
    if (!agentTask) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/agent-tasks/${agentTask.id}/approve`);
      setAgentTask(data.task);
      for (const ca of data.task.client_actions || []) {
        const url = ca.url || (ca.query ? `https://www.google.com/search?q=${encodeURIComponent(ca.query)}` : null);
        if (url) window.open(url, "_blank", "noopener");
      }
      toast.success(`Agent task ${data.task.status}. ${data.task.report}`);
      loadAgentTasks();
    } catch { toast.error("Couldn't run that task."); }
    finally { setBusy(false); }
  };

  const cancelAgent = async () => {
    if (!agentTask) return;
    await api.post(`/agent-tasks/${agentTask.id}/cancel`).catch(() => {});
    setAgentTask(null); loadAgentTasks();
  };

  const buildSchedule = () => {
    const offset_minutes = -new Date().getTimezoneOffset();
    if (autoForm.mode === "interval") return { mode: "interval", interval_minutes: parseInt(autoForm.interval) || 60, offset_minutes };
    if (autoForm.mode === "weekly") return { mode: "weekly", time: autoForm.time, days: autoForm.days, offset_minutes };
    return { mode: "daily", time: autoForm.time, offset_minutes };
  };
  const createAuto = async () => {
    if (!autoForm.title.trim()) { toast.error("Give it a title."); return; }
    try {
      await api.post("/automations", {
        title: autoForm.title.trim(),
        kind: autoForm.mode === "interval" ? "periodic_check" : "recurring",
        action: autoForm.action === "agent_task" ? { type: "agent_task", goal: autoForm.title.trim() } : { type: "notify" },
        schedule: buildSchedule(),
      });
      setShowAutoForm(false);
      setAutoForm({ title: "", mode: "daily", time: "09:00", days: [0], interval: 60, action: "notify" });
      loadAutos(); toast.success("Automation created.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create that automation."); }
  };
  const toggleAuto = async (a) => {
    const { data } = await api.put(`/automations/${a.id}`, { enabled: !a.enabled });
    setAutos((x) => x.map((i) => (i.id === a.id ? data.automation : i)));
  };
  const deleteAuto = async (id) => { await api.delete(`/automations/${id}`); setAutos((x) => x.filter((i) => i.id !== id)); };
  const toggleDay = (d) => setAutoForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] }));

  if (!open) return null;

  const mark = (id, status) => api.post(`/control/activity/${id}/status`, { status }).catch(() => {});

  const interpret = async () => {
    if (!cmdText.trim()) return;
    setBusy(true); setPending(null);
    try {
      const { data } = await api.post("/control/interpret", { text: cmdText.trim(), mode: "text" });
      if (!data.control) { toast("That's not a command I recognise — try 'open youtube' or 'create a folder'."); return; }
      const c = data.control;
      if (c.type === "unsupported") {
        toast.error("That needs the Stealth Desktop Agent — connect your PC in the Devices tab.");
        setTab("devices");
        return;
      }
      // Desktop actions on a connected PC run immediately — no folder prompt, no confirm card.
      if (c.execution_target === "desktop") {
        if (c.status === "sent") { setCmdText(""); executeDesktop(c); return; }
        if (c.status === "agent_offline") { toast.error("Your Desktop Agent is offline. Connect it in the Devices tab."); setTab("devices"); return; }
        if (c.status === "permission_required") { toast("Enable ‘Local file access’ for this device to allow file actions."); setTab("devices"); return; }
        // requires_confirmation (e.g. deleting a file) → show a confirm card
        setPending(c);
        return;
      }
      // Web browser actions (open url / youtube / search) open instantly; web FILE actions use the folder picker.
      if (c.execution_target === "web" && !c.type?.startsWith("file")) { setCmdText(""); runBrowser(c); return; }
      setPending(c);
    } finally { setBusy(false); }
  };

  const runBrowser = (c) => { window.open(c.params.url, "_blank", "noopener"); mark(c.log_id, "executed"); toast.success("Opened in a new tab."); setPending(null); loadHistory(); };

  const pollCmd = async (cid, label) => {
    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const { data } = await api.get(`/agent/commands/${cid}`);
        if (["success", "failed", "blocked", "unsupported", "timeout", "permission_required"].includes(data.status)) {
          if (data.status === "success") toast.success(`Done — ${label}.`);
          else toast.error(`Couldn't complete: ${label} (${data.status}).`);
          loadHistory(); return;
        }
      } catch { return; }
    }
  };

  const executeDesktop = async (c) => {
    if (c.status === "sent" && c.command_id) { toast(`Sent to ${c.device?.name || "your PC"}…`); pollCmd(c.command_id, c.label); return; }
    if (c.status === "pending") {
      try { const { data } = await api.post(`/control/execute/${c.log_id}`); toast(`Sent to ${data.device?.name || "your PC"}…`); pollCmd(data.command_id, c.label); }
      catch (e) {
        const d = e.response?.data?.detail;
        toast.error(d === "agent_offline" ? "Your Desktop Agent is offline." : d === "permission_required" ? "Enable ‘Local file access’ for this device (Devices tab)." : "Couldn't send that.");
      }
    }
  };

  // Dispatch a confirmed control action. Only web FILE actions ever touch the folder picker.
  const runControlCmd = (c) => {
    if (c.execution_target === "web") {
      if (c.type?.startsWith("file")) { runFile(c); return; }
      if (c.params?.url) runBrowser(c);
      else setPending(null);
    } else {
      executeDesktop(c); setPending(null);
    }
  };

  const grant = async () => {
    if (!FS.fsSupported()) { toast.error("Your browser doesn't support local file access. Try Chrome or Edge."); return; }
    try { const d = await FS.pickDirectory(); setDir(d); setEntries(await FS.listEntries(d)); setTab("files"); }
    catch { /* user cancelled */ }
  };

  const refreshFiles = async () => { if (dir) setEntries(await FS.listEntries(dir)); };

  const runFile = async (c) => {
    if (!dir) { toast.error("Grant a folder first (Files tab)."); setTab("files"); return; }
    try {
      if (c.type === "file_create_folder") { await FS.createFolder(dir, c.params.name); toast.success(`Folder “${c.params.name}” created.`); }
      else if (c.type === "file_rename") { await FS.renameEntry(dir, c.params.from, c.params.to); toast.success("Renamed."); }
      else if (c.type === "file_delete") { await FS.deleteEntry(dir, c.params.name); toast.success("Deleted."); }
      else if (c.type === "file_list" || c.type === "file_search") { setTab("files"); }
      else if (c.type === "file_summarize") { await summarizeFile(c.params.name); }
      mark(c.log_id, "executed"); await refreshFiles(); loadHistory();
    } catch (e) { mark(c.log_id, "failed"); toast.error(e.message || "That file action failed."); }
    setPending(null);
  };

  const summarizeFile = async (name) => {
    if (!dir || !name) return;
    setBusy(true); setSummary(null);
    try {
      const text = await FS.readFileText(dir, name);
      const { data } = await api.post("/control/summarize", { text, name });
      setSummary({ name, text: data.summary });
    } catch { toast.error("Couldn't read/summarize that file."); }
    finally { setBusy(false); }
  };

  const createFolderManual = async () => {
    if (!dir || !newFolder.trim()) return;
    try { await FS.createFolder(dir, newFolder.trim()); setNewFolder(""); await refreshFiles(); toast.success("Folder created."); }
    catch (e) { toast.error(e.message); }
  };

  const delFile = async (name) => {
    if (!window.confirm(`Permanently delete “${name}”? This cannot be undone.`)) return;
    try { await FS.deleteEntry(dir, name); await refreshFiles(); toast.success("Deleted."); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <aside data-testid="control-center" className="fixed z-50 top-0 right-0 h-full w-full sm:w-[460px] glass-panel border-l border-white/10 flex flex-col fade-in-up">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div><div className="label-mono">Command Center</div><h2 className="font-heading text-lg font-medium mt-0.5">Stealth Control</h2></div>
          <button data-testid="control-close" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-1.5 p-3 border-b border-white/10 overflow-x-auto thin-scroll">
          {TABS.map((t) => { const Icon = t.icon; return (
            <button key={t.key} data-testid={`control-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`shrink-0 flex items-center justify-center gap-1.5 text-xs py-2 px-3 rounded-full whitespace-nowrap ${tab === t.key ? "text-[#000000] font-medium" : "text-slate-400"}`}
              style={tab === t.key ? { background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" } : {}}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          ); })}
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll p-4 space-y-3">
          {tab === "command" && (
            <>
              <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="w-4 h-4 text-mono-300" /> Only safe, allowlisted actions run — never raw system commands.</div>
              <div className="flex gap-2">
                <input data-testid="control-command-input" value={cmdText} onChange={(e) => setCmdText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && interpret()} placeholder="e.g. open youtube, search the web for…"
                  className="flex-1 rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50" />
                <button data-testid="control-interpret" onClick={interpret} disabled={busy}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-[#000000]" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Terminal className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {["Open YouTube", "Search the web for Stealth AI", "Create a folder called Projects"].map((q) => (
                  <button key={q} onClick={() => setCmdText(q)} className="text-xs text-slate-300 rounded-full px-3 py-1.5 glass-card hover:border-mono-400/40">{q}</button>
                ))}
              </div>

              {pending && (
                <div data-testid="control-pending-card" className="rounded-xl border border-mono-400/30 bg-mono-400/5 p-4">
                  <div className="label-mono !text-mono-300">Stealth is ready to</div>
                  <div className="text-sm mt-1 flex items-center gap-2">
                    {pending.execution_target === "web" && !pending.type?.startsWith("file") ? <Globe className="w-4 h-4 text-mono-300" /> : pending.category === "file" ? <FolderOpen className="w-4 h-4 text-mono-300" /> : <Terminal className="w-4 h-4 text-mono-300" />}
                    {pending.label}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button data-testid="control-confirm" onClick={() => runControlCmd(pending)}
                      className="rounded-full px-4 py-1.5 text-sm text-[#000000] font-medium" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>{pending.execution_target === "web" && !pending.type?.startsWith("file") ? "Open" : "Confirm"}</button>
                    <button data-testid="control-cancel" onClick={() => { mark(pending.log_id, "cancelled"); setPending(null); }}
                      className="rounded-full px-4 py-1.5 text-sm text-slate-300 border border-white/10">Cancel</button>
                  </div>
                </div>
              )}
            </>
          )}

          {tab === "devices" && (
            <>
              <div className="flex items-center gap-2 text-xs text-slate-500"><Monitor className="w-4 h-4 text-mono-300" /> Connect your Windows PC or Mac to run desktop actions. Install the Stealth Desktop Agent, then pair with a code.</div>
              <a data-testid="agent-download" href={`${BACKEND}/api/agent/download`} download className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm glass-card text-mono-300 hover:border-mono-400/50"><Download className="w-4 h-4" /> Download Desktop Agent</a>
              <div>
                <button data-testid="pair-code-button" onClick={genPairCode} className="rounded-full px-4 py-2 text-sm text-[#000000] font-medium flex items-center gap-2" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}><Plug className="w-4 h-4" /> Connect device</button>
                {pairCode && (
                  <div data-testid="pair-code" className="mt-3 rounded-xl border border-mono-400/30 bg-mono-400/5 p-4 text-center">
                    <div className="label-mono !text-mono-300">Pairing code (valid 10 min)</div>
                    <div className="font-heading text-3xl tracking-[0.3em] mt-1">{pairCode}</div>
                    <div className="text-xs text-slate-500 mt-2">1. Install (once): <span className="text-mono-300">Windows — pip install requests selenium pycaw comtypes pyautogui psutil</span> · <span className="text-mono-300">macOS — pip install requests selenium pyautogui psutil</span> (volume uses built-in osascript)</div>
                    <div className="text-xs text-slate-500 mt-1">2. Run: <span className="text-mono-300">python stealth_desktop_agent.py --server {BACKEND} --pair {pairCode}</span></div>
                  </div>
                )}
              </div>
              <div className="space-y-2" data-testid="device-list">
                {devices.length === 0 && <div className="text-sm text-slate-500 text-center py-4">No devices connected yet.</div>}
                {devices.map((d) => (
                  <div key={d.id} data-testid="device-item" className="rounded-xl border border-white/10 p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${d.online ? "bg-mono-400" : "bg-slate-600"}`} />
                        <span className="text-sm">{d.name}</span>
                        <span className="text-[11px] text-slate-500">{d.online ? "Connected" : "Offline"}</span>
                      </div>
                      <button data-testid="device-remove" onClick={() => removeDevice(d.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="mt-3 rounded-lg bg-mono-400/5 border border-mono-400/20 px-3 py-2 flex items-start gap-2">
                      <ShieldCheck className="w-4 h-4 text-mono-300 shrink-0 mt-0.5" />
                      <span className="text-[11px] text-slate-400">Connected — Stealth can control apps, browser, media, volume, keyboard & mouse on this PC. No extra prompts needed.</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between rounded-lg border border-white/10 px-3 py-2">
                      <div>
                        <div className="text-xs text-slate-200 flex items-center gap-1.5"><FolderOpen className="w-3.5 h-3.5 text-mono-300" /> Local file access</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">Let Stealth open, list or organise files on this PC. Off by default.</div>
                      </div>
                      <button data-testid="perm-file" onClick={() => togglePerm(d, "file")}
                        className={`shrink-0 text-[11px] px-3 py-1.5 rounded-full border transition-all ${d.permissions?.file ? "border-mono-400/50 bg-mono-400/10 text-mono-200" : "border-white/10 text-slate-400"}`}>{d.permissions?.file ? "Allowed" : "Off"}</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "files" && (
            <>
              {!dir ? (
                <div className="text-center py-8">
                  <FolderOpen className="w-8 h-8 text-mono-300 mx-auto" />
                  <p className="text-sm text-slate-400 mt-3">Grant Stealth access to one folder. Nothing outside it is ever touched.</p>
                  <button data-testid="control-grant-folder" onClick={grant} className="mt-4 rounded-full px-5 py-2 text-sm text-[#000000] font-medium" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>Choose folder</button>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input data-testid="control-new-folder" value={newFolder} onChange={(e) => setNewFolder(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && createFolderManual()} placeholder="New folder name…"
                      className="flex-1 rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50" />
                    <button data-testid="control-create-folder" onClick={createFolderManual} className="w-10 h-10 rounded-xl flex items-center justify-center text-[#000000]" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}><FolderPlus className="w-4 h-4" /></button>
                  </div>
                  {summary && (
                    <div className="rounded-xl border border-mono-400/20 bg-mono-400/5 p-3">
                      <div className="label-mono !text-mono-300 flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Summary · {summary.name}</div>
                      <div className="text-sm text-slate-200 mt-1 whitespace-pre-wrap">{summary.text}</div>
                    </div>
                  )}
                  <div className="space-y-1" data-testid="control-file-list">
                    {entries.length === 0 && <div className="text-sm text-slate-500 py-4 text-center">Empty folder.</div>}
                    {entries.map((e) => (
                      <div key={e.name} data-testid="control-file-item" className="flex items-center gap-2 rounded-lg px-3 py-2 bg-white/5">
                        {e.kind === "directory" ? <FolderOpen className="w-4 h-4 text-mono-300 shrink-0" /> : <FileText className="w-4 h-4 text-slate-400 shrink-0" />}
                        <span className="text-sm flex-1 truncate">{e.name}</span>
                        {e.kind === "file" && <button title="Summarize" data-testid="control-summarize" onClick={() => summarizeFile(e.name)} className="text-slate-500 hover:text-mono-300">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}</button>}
                        <button title="Delete" data-testid="control-delete-file" onClick={() => delFile(e.name)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {tab === "agent" && (
            <>
              <div className="flex items-center gap-2 text-xs text-slate-500"><Bot className="w-4 h-4 text-mono-300" /> Give a goal. Stealth plans the steps, you approve, then it runs and reports back.</div>
              <div className="flex gap-2">
                <input data-testid="agent-goal-input" value={goal} onChange={(e) => setGoal(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && planAgent()} placeholder="e.g. remind me tomorrow 9am to call the bank and save a note"
                  className="flex-1 rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50" />
                <button data-testid="agent-plan-button" onClick={planAgent} disabled={planning}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-[#000000]" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
                  {planning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>

              {agentTask && (
                <div data-testid="agent-plan-card" className="rounded-xl border border-mono-400/30 bg-mono-400/5 p-4 space-y-3">
                  <div>
                    <div className="label-mono !text-mono-300">Plan</div>
                    <div className="text-sm text-slate-300 mt-1">{agentTask.reasoning}</div>
                  </div>
                  <div className="space-y-1.5" data-testid="agent-steps">
                    {agentTask.steps.map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm rounded-lg px-3 py-2 bg-white/5">
                        <span className="text-[11px] text-mono-300 w-5">{i + 1}.</span>
                        <span className="flex-1 truncate">{s.label}</span>
                        {s.status === "done" && <CheckCircle2 className="w-4 h-4 text-mono-300" />}
                        {s.status === "failed" && <XCircle className="w-4 h-4 text-red-400" />}
                        {(s.status === "skipped" || s.status === "client" || s.status === "queued") && <span className="text-[10px] text-slate-500">{s.status}</span>}
                      </div>
                    ))}
                    {agentTask.steps.length === 0 && <div className="text-sm text-slate-500">No runnable steps.</div>}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-amber-300 flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> Est. {agentTask.credit_estimate} credits</span>
                    {agentTask.status === "awaiting_approval" ? (
                      <div className="flex gap-2">
                        <button data-testid="agent-approve" onClick={approveAgent} disabled={busy || !agentTask.steps.length}
                          className="rounded-full px-4 py-1.5 text-sm text-[#000000] font-medium flex items-center gap-1" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Approve & run
                        </button>
                        <button data-testid="agent-cancel" onClick={cancelAgent} className="rounded-full px-4 py-1.5 text-sm text-slate-300 border border-white/10">Cancel</button>
                      </div>
                    ) : (
                      <span data-testid="agent-report" className={`text-xs px-2 py-1 rounded-full ${agentTask.status === "completed" ? "text-mono-300 bg-mono-500/10" : "text-red-300 bg-red-500/10"}`}>{agentTask.status} · {agentTask.report}</span>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2" data-testid="agent-task-list">
                {agentTasks.map((t) => (
                  <div key={t.id} className="rounded-lg px-3 py-2 bg-white/5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-slate-200 truncate">{t.goal}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${t.status === "completed" ? "text-mono-300 bg-mono-500/10" : t.status === "failed" ? "text-red-300 bg-red-500/10" : t.status === "awaiting_approval" ? "text-amber-300 bg-amber-500/10" : "text-slate-400 bg-white/5"}`}>{t.status}</span>
                    </div>
                    {t.report && <div className="text-[11px] text-slate-500 mt-0.5">{t.report}</div>}
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "automations" && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-slate-500"><Zap className="w-4 h-4 text-mono-300" /> Scheduled & recurring tasks. Stealth notifies you when they're due.</div>
                <button data-testid="automation-new" onClick={() => setShowAutoForm((s) => !s)} className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[#000000]" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}><Plus className="w-4 h-4" /></button>
              </div>

              {showAutoForm && (
                <div data-testid="automation-form" className="rounded-xl border border-white/10 p-3 space-y-3">
                  <input data-testid="automation-title" value={autoForm.title} onChange={(e) => setAutoForm({ ...autoForm, title: e.target.value })}
                    placeholder="What should Stealth do? e.g. Check my business" className="w-full rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50" />
                  <div className="flex gap-2">
                    {["daily", "weekly", "interval"].map((m) => (
                      <button key={m} data-testid={`automation-mode-${m}`} onClick={() => setAutoForm({ ...autoForm, mode: m })}
                        className={`flex-1 text-xs capitalize rounded-lg py-1.5 border ${autoForm.mode === m ? "border-mono-400/50 bg-mono-400/10 text-mono-200" : "border-white/10 text-slate-400"}`}>{m === "interval" ? "Periodic" : m}</button>
                    ))}
                  </div>
                  {autoForm.mode === "interval" ? (
                    <label className="text-xs text-slate-400 flex items-center gap-2">Every <input type="number" min="1" data-testid="automation-interval" value={autoForm.interval} onChange={(e) => setAutoForm({ ...autoForm, interval: e.target.value })} className="w-20 rounded-lg bg-[#0D0D0D] border border-white/10 px-2 py-1 text-sm" /> minutes</label>
                  ) : (
                    <label className="text-xs text-slate-400 flex items-center gap-2">At <input type="time" data-testid="automation-time" value={autoForm.time} onChange={(e) => setAutoForm({ ...autoForm, time: e.target.value })} className="rounded-lg bg-[#0D0D0D] border border-white/10 px-2 py-1 text-sm" /></label>
                  )}
                  {autoForm.mode === "weekly" && (
                    <div className="flex gap-1">
                      {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                        <button key={i} onClick={() => toggleDay(i)} className={`w-8 h-8 rounded-full text-xs ${autoForm.days.includes(i) ? "bg-mono-400/20 text-mono-200 border border-mono-400/50" : "border border-white/10 text-slate-400"}`}>{d}</button>
                      ))}
                    </div>
                  )}
                  <button data-testid="automation-create" onClick={createAuto} className="w-full rounded-full py-2 text-sm text-[#000000] font-medium" style={{ background: "linear-gradient(90deg,#FFFFFF,#C8C8C8)" }}>Create automation</button>
                </div>
              )}

              <div className="space-y-2" data-testid="automation-list">
                {autos.length === 0 && !showAutoForm && <div className="text-sm text-slate-500 text-center py-8">No automations yet.</div>}
                {autos.map((a) => (
                  <div key={a.id} data-testid="automation-item" className="rounded-xl border border-white/10 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm text-slate-100 truncate">{a.title}</div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" /> {a.next_run ? new Date(a.next_run).toLocaleString() : "—"}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button data-testid="automation-toggle" onClick={() => toggleAuto(a)} className={`text-[11px] px-2 py-1 rounded-full ${a.enabled ? "text-mono-300 bg-mono-500/10" : "text-slate-400 bg-white/5"}`}>{a.enabled ? "On" : "Off"}</button>
                        <button data-testid="automation-delete" onClick={() => deleteAuto(a.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === "history" && (
            <div className="space-y-2" data-testid="control-activity-list">
              {history.length === 0 && <div className="text-sm text-slate-500 text-center py-8">No activity yet.</div>}
              {history.map((a, i) => (
                <div key={i} data-testid="control-activity-item" className="rounded-lg px-3 py-2 bg-white/5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-slate-200 truncate">{a.title}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${["executed", "completed", "ran", "success"].includes(a.status) ? "text-mono-300 bg-mono-500/10" : ["failed", "unsupported"].includes(a.status) ? "text-red-300 bg-red-500/10" : a.status === "cancelled" ? "text-slate-400 bg-white/5" : "text-amber-300 bg-amber-500/10"}`}>{a.status}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 capitalize">{a.source}{a.detail ? ` · ${a.detail}` : ""} · {a.at ? new Date(a.at).toLocaleString() : ""}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
